/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { isBoolean, isUndefined } from 'lodash';
import { PluginType, type BaseClassInstance, type IExportPlugin, type IRegisterPluginOptions } from '@/version-types';
import { type IService } from '../../shared';
import type { ServiceManager } from '../service-manager';

/** 注册插件服务 */
export class RegistrationService implements IService {
    private __systemPlugins: Record<string, IExportPlugin> = {};
    private __viewportPlugins: Record<string, IExportPlugin> = {};
    private __stagePlugins: Record<string, IExportPlugin> = {};
    /** 代码实例 */
    private __instances: Map<string, unknown> = new Map();
    /** 已加载插件列表 */
    private __loadedList: Set<string> = new Set();
    /** 禁用的列表 */
    private __disableList: Set<string> = new Set();

    /** 数据初始化列表 */
    private __destroyList: ((args: { identity: string; getInstance: (classConstructor: any) => any }) => void)[] = [];
    /** 历史记录 */
    private __historyList: ((args: { identity: string }) => InstanceType<BaseClassInstance>[])[] = [];

    /** 状态列表 */
    readonly states: Record<string, any> = {};

    constructor(readonly service: ServiceManager) {}

    onInit() {
        this.__destroyList.forEach((fn) =>
            fn({ identity: this.service.identity, getInstance: this.getInstance.bind(this) }),
        );
    }

    onCreate() {
        this.__historyList.forEach((fn) => fn({ identity: this.service.identity }));
    }

    /**
     * 根据类名获取实例 - 重载2：传入字符串名称，返回泛型类型
     */
    getInstance<T = any>(key: string): T {
        return this.__instances.get(key) as T;
    }

    /** 获取插件 */
    getPlugins() {
        return {
            system: Object.values(this.__systemPlugins),
            viewport: Object.values(this.__viewportPlugins),
            stage: Object.values(this.__stagePlugins),
        };
    }

    /** 设置插件 */
    registerPlugins(configs: IRegisterPluginOptions[], rootIsEnable?: boolean) {
        for (const config of configs) {
            const plugin = config.plugin;
            const name = plugin.name;

            if (!name) {
                console.error(`插件 ${name} 未设置 name 属性`);
                continue;
            }
            // console.log('---===>', this.__loadedList);
            const isEnable = isUndefined(rootIsEnable) ? (config.enable ?? true) : rootIsEnable;
            this.__registerInstance(plugin, isEnable);

            if (plugin.type === PluginType.LAYER && !plugin.createBehavior) {
                throw new Error(`${name} 插件需要实现 createBehavior 方法`);
            }
            if (plugin.type === PluginType.EXTENSION) continue;
            if (isBoolean(rootIsEnable) && !rootIsEnable) continue;
            if (!plugin.render) continue;

            // 设置功能插件
            if (config.enable ?? true) {
                if (this.__disableList.has(name)) continue;
                if (plugin.type === PluginType.SYSTEM) {
                    this.__systemPlugins[name] = plugin;
                } else if (plugin.type === PluginType.VIEWPORT) {
                    this.__viewportPlugins[name] = plugin;
                } else if (plugin.type === PluginType.STAGE) {
                    this.__stagePlugins[name] = plugin;
                }
            } else {
                this.__disableList.add(name);
                if (plugin.type === PluginType.SYSTEM) {
                    delete this.__systemPlugins[name];
                } else if (plugin.type === PluginType.VIEWPORT) {
                    delete this.__viewportPlugins[name];
                } else if (plugin.type === PluginType.STAGE) {
                    delete this.__stagePlugins[name];
                }
            }
        }

        console.log('registerPlugins=>', this.getPlugins());
    }

    /** 设置实例 */
    private __setInstance<T extends BaseClassInstance>(name: string, instance: InstanceType<T>) {
        if (this.__instances.get(name)) {
            console.error(`${name} 实例已存在，请检查是否重名，或者重复实例化`);
        }
        this.__instances.set(name, instance);
    }

    /** 创建插件代码实例 */
    private __registerInstance(plugin: IExportPlugin, rootIsEnable: boolean) {
        if (this.__loadedList.has(plugin.name)) {
            console.log(`⚠️${plugin.name} 依赖加载`);
            return;
        }
        console.log(`✔ ${plugin.name} 初始化`);
        this.__loadedList.add(plugin.name);

        // 加载自定义历史记录
        if (plugin.createHistory) {
            this.__historyList.push(plugin.createHistory);
        }

        // 加载状态
        if (plugin.createState) {
            const res = plugin.createState();
            for (const item of res) {
                if (this.states[item.name]) {
                    throw new Error(`${item.name} 状态已存在`);
                }
                this.states[item.name] = item.createState(this.service.identity);
            }
        }

        // 加载依赖插件
        if (plugin.dependencies) {
            const res = plugin.dependencies();
            this.registerPlugins(res, rootIsEnable);
        }

        // 数据初始化
        if (plugin.destroy) {
            this.__destroyList.push(plugin.destroy);
        }

        // 创建代码实例
        if (plugin.createInstance) {
            const res = plugin.createInstance({ identity: this.service.identity });
            console.log('createInstance=>', res);
            for (const instance of res) {
                if (!instance.key || !instance.value) {
                    console.error(`${plugin.name} 插件实例缺少 key 或 value 属性`);
                } else {
                    this.__setInstance(instance.key, instance.value);
                    const name = instance.value.constructor.name;
                    if (name !== instance.key) {
                        this.__setInstance(name, instance.value);
                    }
                }
            }
        }

        // 创建图层行为
        if (plugin.createBehavior) {
            const item = plugin.createBehavior();
            if (!item?.layerType) {
                throw new Error(`${plugin.name} 插件缺少 layerType 属性`);
            }
            const behaviorObj = item.getBehavior(this.service.identity);
            const key = this.service.behavior.getKey(item.layerType, behaviorObj.role);
            // console.log('key=>', key);
            if (this.service.behavior.getByKey(key)) {
                console.log(`${item.layerType} 行为已注册过`);
                return;
            }

            this.service.behavior.setByKey(key, behaviorObj);
        }
    }
}
